/**
 * F-012 — Trajectory preview sampler.
 *
 * Forward-Euler integrates a virtual planet's path under the gravity well
 * for ``steps`` ticks of size ``dt``. Used by the trajectory line renderer
 * to draw a dotted preview during slingshot aim. The simulation is stand-
 * alone (no cannon body) so we can run it cheap every frame without
 * polluting the real physics world.
 */

import type { GravityWell } from "../physics/gravity-well.ts";

export interface SamplePoint {
  x: number;
  y: number;
  z: number;
}

export interface SampleTrajectoryOptions {
  origin: SamplePoint;
  velocity: SamplePoint;
  well: GravityWell;
  steps?: number;
  dt?: number;
  /** Optional bounding sphere — if a sample exits, sampling stops. */
  maxRadius?: number;
}

const DEFAULT_STEPS = 30;
const DEFAULT_DT = 0.06;
const VIRTUAL_MASS = 1; // matches a tier-0 planet for preview symmetry

export function sampleTrajectory(
  opts: SampleTrajectoryOptions,
): SamplePoint[] {
  const steps = opts.steps ?? DEFAULT_STEPS;
  const dt = opts.dt ?? DEFAULT_DT;
  const maxRadius = opts.maxRadius ?? Number.POSITIVE_INFINITY;

  const out: SamplePoint[] = [];
  let px = opts.origin.x;
  let py = opts.origin.y;
  let pz = opts.origin.z;
  let vx = opts.velocity.x;
  let vy = opts.velocity.y;
  let vz = opts.velocity.z;

  const center = opts.well.center;
  const strength = opts.well.strength;
  const wellMaxRadius = opts.well.maxRadius;
  const wellMinDist = opts.well.minDistance;

  for (let i = 0; i < steps; i += 1) {
    out.push({ x: px, y: py, z: pz });

    const dx = center.x - px;
    const dy = center.y - py;
    const dz = center.z - pz;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq <= wellMaxRadius * wellMaxRadius) {
      const dist = Math.max(Math.sqrt(distSq), wellMinDist);
      const accel = (strength * VIRTUAL_MASS) / (dist * dist);
      const inv = 1 / dist;
      vx += dx * inv * accel * dt;
      vy += dy * inv * accel * dt;
      vz += dz * inv * accel * dt;
    }

    px += vx * dt;
    py += vy * dt;
    pz += vz * dt;

    if (px * px + py * py + pz * pz > maxRadius * maxRadius) break;
  }

  return out;
}

export const __testing = { DEFAULT_STEPS, DEFAULT_DT, VIRTUAL_MASS };
